import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useLocation } from 'react-router-dom';

import { isSettingsPath } from '../../../components/SettingsModal/useSettingsBackground';
import { CacheKey } from '../../common/constants';
import { startPage } from '../../common/readerActivity';
import reloadDocument from '../../common/reloadDocument';
import { isReloadHeld, isWorkHeld } from '../../common/reloadHolds';
import { hasUnsavedChanges } from './useRefreshWarning';
import useReloadWhenQuiet from './useReloadWhenQuiet';

export const VERSION_CHECK_INTERVAL_MS = 10 * 60 * 1000;

/** The least time between two checks, so a burst of page changes or focus events costs one request. */
export const VERSION_CHECK_MIN_GAP_MS = 60 * 1000;

/**
 * The least time between two reloads onto a new build. A reload that lands on
 * the build it left — CloudFront briefly serving the previous `index.html` —
 * would otherwise reload again on every page change.
 */
export const NEW_BUILD_RELOAD_COOLDOWN_MS = 5 * 60 * 1000;

/** How long nobody uses a tab before it takes a new build without a change of page. */
export const NEW_BUILD_QUIET_RELOAD_MS = 30 * 60 * 1000;

const VERSION_URL = '/version.json';

/** The ID `scripts/stampBuildId.js` stamps into `index.html`; absent under the dev server. */
const readRunningBuildId = () =>
  document.querySelector<HTMLMetaElement>('meta[name="build-id"]')?.content || undefined;

const fetchDeployedBuildId = async (): Promise<string | undefined> => {
  try {
    const response = await fetch(VERSION_URL, { cache: 'no-store' });
    if (!response.ok) {
      return undefined;
    }
    const body: unknown = await response.json();
    const buildId = (body as { buildId?: unknown } | null)?.buildId;
    return typeof buildId === 'string' && buildId ? buildId : undefined;
  } catch {
    return undefined;
  }
};

/** Claims this tab's reload onto a new build, or refuses while the last one is recent. */
const claimReload = (): boolean => {
  try {
    const last = Number(sessionStorage.getItem(CacheKey.NewBuildReloadedAt));
    if (Date.now() - last < NEW_BUILD_RELOAD_COOLDOWN_MS) {
      return false;
    }
    sessionStorage.setItem(CacheKey.NewBuildReloadedAt, String(Date.now()));
    return true;
  } catch {
    // Storage that cannot be read or written cannot bound a reload loop
    return false;
  }
};

/**
 * Moves an open tab onto a newly deployed build at the next change of page,
 * where a full page load costs the reader nothing. Search params alone, and the
 * settings modal opening over the page behind it, are not a change of page. The
 * reload waits for a later one while offline, while either page holds unsaved
 * changes or work (`reloadHolds`), or while the page being left has a read in
 * flight. A tab nobody has used for half an hour takes the build where it is
 * (`useReloadWhenQuiet`).
 */
const useReloadOnNewBuild = () => {
  const { pathname } = useLocation();
  const [runningBuildId] = useState(readRunningBuildId);
  const isNewBuildDeployed = useRef(false);
  const lastCheckAt = useRef(0);
  const previousPathname = useRef(pathname);

  // Read in render, while the page being left is still mounted and before the
  // page arrived at has started requests of its own
  const departure = useMemo(
    () => ({ pathname, isBusy: hasUnsavedChanges() || isReloadHeld() }),
    [pathname]
  );

  const getWantedSince = useCallback(() => (isNewBuildDeployed.current ? 0 : undefined), []);
  useReloadWhenQuiet({
    getWantedSince,
    quietForMs: NEW_BUILD_QUIET_RELOAD_MS,
    claim: claimReload,
  });

  const check = useCallback(async () => {
    if (Date.now() - lastCheckAt.current < VERSION_CHECK_MIN_GAP_MS) {
      return;
    }
    lastCheckAt.current = Date.now();

    const deployedBuildId = await fetchDeployedBuildId();
    // A failed check says nothing about the deploy, so it leaves the last answer standing
    if (deployedBuildId) {
      isNewBuildDeployed.current = deployedBuildId !== runningBuildId;
    }
  }, [runningBuildId]);

  useEffect(() => {
    if (!runningBuildId) {
      return undefined;
    }

    const checkIfVisible = () => {
      if (document.visibilityState === 'visible') {
        void check();
      }
    };

    const interval = window.setInterval(checkIfVisible, VERSION_CHECK_INTERVAL_MS);
    document.addEventListener('visibilitychange', checkIfVisible);
    window.addEventListener('focus', checkIfVisible);

    return () => {
      window.clearInterval(interval);
      document.removeEventListener('visibilitychange', checkIfVisible);
      window.removeEventListener('focus', checkIfVisible);
    };
  }, [check, runningBuildId]);

  useEffect(() => {
    if (isSettingsPath(departure.pathname) || departure.pathname === previousPathname.current) {
      return;
    }
    previousPathname.current = departure.pathname;
    startPage();

    if (!runningBuildId) {
      return;
    }

    if (
      isNewBuildDeployed.current &&
      navigator.onLine &&
      !departure.isBusy &&
      !hasUnsavedChanges() &&
      !isWorkHeld() &&
      claimReload()
    ) {
      reloadDocument();
      return;
    }

    void check();
  }, [check, departure, runningBuildId]);
};

export default useReloadOnNewBuild;
